import React, { useEffect, useRef } from 'react';

const initializedAdSlots = new Set();

/**
 * AdSenseAd Component - Properly initializes Google AdSense ad units
 * 
 * Fixes zero-width initialization error by:
 * 1. Waiting for container to have measurable width
 * 2. Retrying after layout settles without observing layout changes
 * 3. Only pushing to adsbygoogle once per ad slot
 * 4. Preventing duplicate initialization
 */
const AdSenseAd = ({ 
  adClient = "ca-pub-6039804412418359",
  adSlot,
  adFormat = "auto",
  fullWidthResponsive = true,
  style = {},
  label = "Advertisement"
}) => {
  const adRef = useRef(null);
  const containerRef = useRef(null);
  const initializedRef = useRef(false);
  const initAttemptRef = useRef(0);

  useEffect(() => {
    // If no ad slot, don't do anything
    if (!adSlot) return;

    initializedRef.current = false;
    initAttemptRef.current = 0;
    let retryTimer;

    // Function to initialize the ad
    const initializeAd = () => {
      if (initializedRef.current) return;
      if (!containerRef.current || !adRef.current) return;
      if (initializedAdSlots.has(adSlot)) {
        initializedRef.current = true;
        return;
      }
      
      const width = containerRef.current.offsetWidth;
      if (width < 100) {
        // Container doesn't have sufficient width yet, retry
        initAttemptRef.current++;
        if (initAttemptRef.current < 20) {
          retryTimer = setTimeout(initializeAd, 200);
        }
        return;
      }

      try {
        // Mark before the AdSense push without triggering a layout render.
        initializedRef.current = true;
        initializedAdSlots.add(adSlot);
        
        // Push to adsbygoogle
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        console.log(`[AdSense] Initialized slot ${adSlot} with width ${width}px`);
      } catch (error) {
        initializedAdSlots.delete(adSlot);
        initializedRef.current = false;
        console.error('[AdSense] Error initializing ad:', error);
      }
    };

    // Layout-safe startup retries avoid ResizeObserver feedback loops from ad rendering.
    const timers = [
      setTimeout(initializeAd, 500),
      setTimeout(initializeAd, 1500),
      setTimeout(initializeAd, 3000)
    ];

    return () => {
      timers.forEach(t => clearTimeout(t));
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [adSlot]);

  // If no ad slot provided, don't render
  if (!adSlot) {
    return null;
  }

  return (
    <div 
      ref={containerRef}
      className="ad-container" 
      style={{ minHeight: '90px', overflow: 'hidden', minWidth: 0, width: '100%', maxWidth: '300px', marginInline: 'auto' }}
      data-testid={`ad-container-${adSlot}`}
    >
      {label && (
        <div className="text-center text-[10px] text-gray-400 uppercase tracking-wider mb-1">
          {label}
        </div>
      )}
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', ...style }}
        data-ad-client={adClient}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidthResponsive.toString()}
        data-testid={`ad-slot-${adSlot}`}
      />
    </div>
  );
};

export default AdSenseAd;
